/**
 * Memory monitoring utilities for tracking application memory usage
 *
 * Provides hooks and utilities to monitor memory usage and trigger cleanup
 * when memory pressure is detected.
 */

export interface MemoryStats {
  usedJSHeapSize: number; // Bytes
  totalJSHeapSize: number; // Bytes
  jsHeapSizeLimit: number; // Bytes
  usagePercentage: number; // 0-100
}

/**
 * Gets current memory usage statistics (Chrome only)
 * Returns null if memory API is not available
 */
export function getMemoryStats(): MemoryStats | null {
  // @ts-expect-error - performance.memory is Chrome-specific
  const memory = performance.memory;

  if (!memory) {
    return null;
  }

  const usedJSHeapSize = memory.usedJSHeapSize || 0;
  const jsHeapSizeLimit = memory.jsHeapSizeLimit || 0;
  const usagePercentage = jsHeapSizeLimit > 0 ? (usedJSHeapSize / jsHeapSizeLimit) * 100 : 0;

  return {
    usedJSHeapSize,
    totalJSHeapSize: memory.totalJSHeapSize || 0,
    jsHeapSizeLimit,
    usagePercentage,
  };
}

/**
 * Memory pressure levels
 */
export enum MemoryPressure {
  LOW = "low", // < 60% usage
  MEDIUM = "medium", // 60-80% usage
  HIGH = "high", // 80-90% usage
  CRITICAL = "critical", // > 90% usage
}

/**
 * Gets current memory pressure level
 */
export function getMemoryPressure(stats: MemoryStats | null = getMemoryStats()): MemoryPressure {
  if (!stats) {
    return MemoryPressure.LOW;
  }

  const { usagePercentage } = stats;

  if (usagePercentage >= 90) return MemoryPressure.CRITICAL;
  if (usagePercentage >= 80) return MemoryPressure.HIGH;
  if (usagePercentage >= 60) return MemoryPressure.MEDIUM;
  return MemoryPressure.LOW;
}
